/**
 * joinRoom.js
 * 
 * Helpers to help user join a room
 */

import { doc, getDoc, onSnapshot } from "firebase/firestore"
import { db } from "../../config/firebase"

/**
 * fetchRoomDetails
 * 
 * Fetches details of a specific Auction Room from Firestore.
 * 
 * @param {String} dbCollection -   The name of the game / the collection in the root folder
 * @param {string} roomID - The unique ID of the auction room to fetch.
 * @returns {object|{}} - Returns room data if found, otherwise an empty object.
 */
export const fetchRoomDetails = async (dbCollection, roomID) => {
    try {
        const roomRef = doc(db, dbCollection, roomID);
        const roomSnap = await getDoc(roomRef);

        // --- Return room details or empty object ---
        if (roomSnap.exists()) {
            return {
                ...roomSnap.data(),
                id : roomSnap.id
            };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error in fetching room:", error);
        return null;
    }
};

/**
 * subscribeToRoom
 * 
 * Listens to a room document, calling onChange with the latest room (or null if it doesn't exist).
 * 
 * @param {String} dbCollection -   The name of the game / the collection in the root folder
 * @param {string} roomID       -   The unique ID of the room to listen to.
 * @param {Function} onChange   -   Called with the room data, or null.
 * @returns {Function}          -   Unsubscribe function.
 */
export const subscribeToRoom = (dbCollection, roomID, onChange) => {
    const roomRef = doc(db, dbCollection, roomID);
    return onSnapshot(
        roomRef,
        (roomSnap) => onChange(roomSnap.exists() ? { ...roomSnap.data(), id: roomSnap.id } : null),
        (error) => {
            console.error("Error listening to room:", error);
            onChange(null);
        }
    );
};
